import fontkit from "@pdf-lib/fontkit";
import {
  PDFCheckBox,
  PDFDocument,
  PDFDropdown,
  PDFOptionList,
  PDFRadioGroup,
  PDFTextField,
} from "pdf-lib";

const formFontUrl = require("dejavu-fonts-ttf/ttf/DejaVuSans.ttf");

function decodePdfString(value) {
  if (!value) return "";
  if (typeof value.decodeText === "function") return value.decodeText();
  if (typeof value.asString === "function") return value.asString();
  return String(value);
}

function fieldLabel(field) {
  const acroField = field.acroField;
  if (!acroField) return "";
  return decodePdfString(acroField.getAlternateName?.())
    || decodePdfString(acroField.getMappingName?.())
    || "";
}

function fieldType(field) {
  if (field instanceof PDFTextField) return "text";
  if (field instanceof PDFCheckBox) return "checkbox";
  if (field instanceof PDFRadioGroup) return "radio";
  if (field instanceof PDFDropdown) return "dropdown";
  if (field instanceof PDFOptionList) return "option_list";
  return "unknown";
}

function fieldOptions(field) {
  if (
    field instanceof PDFRadioGroup
    || field instanceof PDFDropdown
    || field instanceof PDFOptionList
  ) {
    return field.getOptions();
  }
  if (field instanceof PDFCheckBox) {
    const onValue = field.acroField?.getOnValue?.();
    const value = decodePdfString(onValue);
    return value ? [value] : [];
  }
  return [];
}

function currentValue(field) {
  if (field instanceof PDFTextField) return field.getText() || "";
  if (field instanceof PDFCheckBox) return field.isChecked();
  if (field instanceof PDFRadioGroup) return field.getSelected() || "";
  if (field instanceof PDFDropdown || field instanceof PDFOptionList) {
    return field.getSelected();
  }
  return "";
}

export async function inspectApplicationPdf(templateBytes) {
  const pdfDoc = await PDFDocument.load(templateBytes, {ignoreEncryption: true});
  const fields = [];
  pdfDoc.getForm().getFields().forEach((field) => {
    try {
      const type = fieldType(field);
      if (type === "unknown") return;
      fields.push({
        name: field.getName(),
        type,
        label: fieldLabel(field),
        options: fieldOptions(field).slice(0, 100),
        current_value: currentValue(field),
      });
    } catch (error) {
      // Ignore a single malformed bank field and keep the usable form fields.
    }
  });

  return {pdfDoc, fields};
}

function checkboxShouldBeChecked(value) {
  return ["true", "1", "yes", "ano", "áno", "on", "checked", "x"]
    .includes(String(value || "").trim().toLocaleLowerCase("sk"));
}

function selectMatchingOption(field, requestedValue) {
  const options = field.getOptions();
  const requested = String(requestedValue || "").trim();
  const exact = options.find((option) => option === requested);
  if (exact) return exact;
  const normalized = requested.toLocaleLowerCase("sk");
  return options.find(
    (option) => String(option).trim().toLocaleLowerCase("sk") === normalized,
  );
}

function selectMatchingOptions(field, requestedValue) {
  const values = Array.isArray(requestedValue) ? requestedValue : [requestedValue];
  return values
    .map((value) => selectMatchingOption(field, value))
    .filter(Boolean);
}

function applyValue(field, value) {
  if (field instanceof PDFTextField) {
    field.setText(String(value));
    return true;
  }
  if (field instanceof PDFCheckBox) {
    if (checkboxShouldBeChecked(value)) field.check();
    else field.uncheck();
    return true;
  }
  if (
    field instanceof PDFRadioGroup
    || field instanceof PDFDropdown
    || field instanceof PDFOptionList
  ) {
    const options = selectMatchingOptions(field, value);
    if (!options.length) return false;
    if (field instanceof PDFOptionList && Array.isArray(value)) {
      field.select(options);
    } else {
      field.select(options[0]);
    }
    return true;
  }
  return false;
}

async function saveApplicationPdf(pdfDoc, fieldValues, requireAppliedValue) {
  pdfDoc.registerFontkit(fontkit);
  const fontResponse = await fetch(formFontUrl);
  if (!fontResponse.ok) {
    throw new Error("Nepodarilo sa načítať písmo pre slovenskú diakritiku.");
  }
  const font = await pdfDoc.embedFont(await fontResponse.arrayBuffer(), {subset: true});
  const form = pdfDoc.getForm();
  let appliedCount = 0;

  (fieldValues || []).forEach(({name, value}) => {
    try {
      const field = form.getField(name);
      if (applyValue(field, value)) appliedCount += 1;
    } catch (error) {
      // A single read-only or malformed bank field must not discard the rest.
    }
  });

  if (requireAppliedValue && appliedCount === 0) {
    throw new Error("AI nenašla žiadne údaje, ktoré sa dali zapísať do PDF.");
  }

  try {
    form.updateFieldAppearances(font);
  } catch (error) {
    // Some bank PDFs contain malformed appearance definitions. The actual
    // AcroForm values still remain editable in the PDF viewer.
  }
  const bytes = await pdfDoc.save({useObjectStreams: false});
  return {
    blob: new Blob([bytes], {type: "application/pdf"}),
    appliedCount,
  };
}

export async function fillApplicationPdf(pdfDoc, fieldValues) {
  return saveApplicationPdf(pdfDoc, fieldValues, true);
}

export async function saveEditedApplicationPdf(pdfBlob, fieldValues) {
  const bytes = pdfBlob instanceof Blob
    ? await pdfBlob.arrayBuffer()
    : pdfBlob;
  const pdfDoc = await PDFDocument.load(bytes, {ignoreEncryption: true});
  return saveApplicationPdf(pdfDoc, fieldValues, false);
}
